import {
  Chip,
  Grid2,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";

import { useMemo, useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { PayableMonth, PayablesPaid } from "../services/payable";
import { MRT_ColumnDef } from "material-react-table";
import { formatPrice } from "../helpers/format";
import VirtualizedTable from "../components/VirtualizeTable";
import { useUserStore } from "../stores/user";

import moment from "moment";
import DateRanger from "./DateRanger";
import { IconEdit, IconTrash } from "@tabler/icons-react";
import PayableEdit from "./PayableEdit";
import PayableDelete from "./PayableDelete";
import { useQueryClient } from "@tanstack/react-query";

const PayableOverdues = () => {
  const { user } = useUserStore();
  const [editOpen, setEditOpen] = useState(false);
  const [selectedPayable, setSelectedPayable] = useState(null);
  const [id, setId] = useState("");
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();

  const [filters, setFilters] = useState({
    startDate: moment().startOf("year").toDate(),
    endDate: moment().endOf("year").toDate(),
  });

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["payablePaids", user?.storeId, filters],
    queryFn: async () =>
      await PayablesPaid({ storeId: user?.storeId, ...filters }),
    initialData: {
      month: [],
      recurrence: [],
      countPaid: [],
    },
  });

  const mergedData = useMemo(() => {
    return [...(data?.month || []), ...(data?.recurrence || [])];
  }, [data]);

  type PayableItem = Awaited<ReturnType<typeof PayableMonth>>["month"][0];

  const afterChange = () => {
    refetch();
    queryClient.invalidateQueries({ queryKey: ["indicators", user?.storeId] });
  };

  const columns = useMemo<MRT_ColumnDef<PayableItem>[]>(() => {
    return [
      {
        accessorKey: "status",
        header: "Status",
        size: 90,
        Cell: ({ cell }) => {
          const status = cell.getValue() as string;
          let color: any = "";
          let label = status;

          if (status === "PENDING") {
            color = "warning";
            label = "Pendente";
          }

          if (status === "PAID") {
            color = "success";
            label = "Pago";
          }

          if (status === "OVERDUE") {
            label = "Atrasado";
            color = " #d35400";
          }

          if (status === "SCHEDULED") {
            color = "gray";
            label = "Agendado";
          }

          if (status === "CANCELED") {
            color = "error";
            label = "Cancelado";
          }

          return (
            <Chip
              label={label}
              color={
                ["warning", "success", "error", "info"].includes(color)
                  ? color
                  : undefined
              }
              sx={{
                bgcolor: !["warning", "success", "error", "info"].includes(
                  color
                )
                  ? color
                  : undefined,
                color: "#fff",
                width: "100%",
              }}
            />
          );
        },
        muiTableBodyCellProps: { align: "center", sx: { textAlign: "center" } },
      },
      {
        accessorKey: "title",
        header: "Conta",
        size: 210,
      },
      {
        accessorKey: "amount",
        header: "Valor",
        size: 70,
        Cell: ({ cell }) => formatPrice(cell.getValue() as number) || "--",
        muiTableHeadCellProps: { align: "center", sx: { textAlign: "center" } },
        muiTableBodyCellProps: { align: "center", sx: { textAlign: "center" } },
      },
      {
        accessorKey: "dueDate",
        header: "Vencimento",
        size: 110,
        Cell: ({ cell }) => {
          const v = cell.getValue() as string | null;
          return v ? moment.utc(v).format("DD/MM/YYYY") : "--";
        },
        muiTableBodyCellProps: {
          align: "center",
          sx: { textAlign: "center" },
        },
        muiTableHeadCellProps: {
          align: "center",
          sx: {
            textAlign: "center",
            overflow: "visible",
          },
        },
      },
      {
        accessorKey: "paymentDate",
        header: "Pagamento",
        size: 110,
        Cell: ({ cell }) => {
          const v = cell.getValue() as string | null;
          return v ? moment.utc(v).format("DD/MM/YYYY") : "--";
        },
        muiTableHeadCellProps: {
          align: "center",
          sx: {
            textAlign: "center",
            overflow: "visible",
          },
        },
        muiTableBodyCellProps: { align: "center", sx: { textAlign: "center" } },
      },
      {
        id: "editar",
        header: "Editar",
        size: 50,
        Cell: ({ row }) => {
          return (
            <Stack direction="row" spacing={1} justifyContent="center">
              <Tooltip title="Editar">
                <IconButton
                  size="small"
                  onClick={() => {
                    setSelectedPayable(row.original);
                    setEditOpen(true);
                  }}
                >
                  <IconEdit size={16} />
                </IconButton>
              </Tooltip>
            </Stack>
          );
        },
        muiTableHeadCellProps: { align: "center", sx: { textAlign: "center" } },
        muiTableBodyCellProps: { align: "center", sx: { textAlign: "center" } },
      },
      {
        id: "delete",
        header: "Excluir",
        size: 50,
        Cell: ({ row }: any) => (
          <IconTrash
            onClick={() => {
              setId(row.original.id);
              setOpen(true);
            }}
            style={{ cursor: "pointer", color: "#d32f2f" }}
          />
        ),
        muiTableHeadCellProps: { align: "center", sx: { textAlign: "center" } },
        muiTableBodyCellProps: { align: "center", sx: { textAlign: "center" } },
        enableSorting: false,
        enableColumnActions: false,
      },
    ];
  }, [mergedData]);

  return (
    <>
      <PayableDelete
        open={open}
        id={id}
        onClose={() => setOpen(false)}
        onAction={afterChange}
      />
      {editOpen && (
        <PayableEdit
          open={editOpen}
          onClose={() => setEditOpen(false)}
          refetch={afterChange}
          payable={selectedPayable}
          paids
          isRecurring
        />
      )}
      <Grid2 size={12}>
        <Grid2
          size={12}
          sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}
        >
          <Stack
            sx={{
              width: "100%",
              flexDirection: "row",
              justifyContent: "space-between",
              alignContent: "center",
            }}
          >
            <Grid2 size={{ xs: 12, md: 6 }}>
              <Typography fontSize={18} fontWeight={700}>
                Contas pagas
              </Typography>{" "}
              <Typography
                fontSize={{ xs: 12, sm: 14 }}
                color="secondary.contrastText"
              >
                Total de contas pagas: {data?.countPaid || 0}
              </Typography>
            </Grid2>
            <Grid2 size={6}>
              <Stack>
                <DateRanger
                  start_date={filters.startDate}
                  end_date={filters.endDate}
                  setstart_date={(i: any) => {
                    setFilters((e: any) => ({
                      ...e,
                      startDate: moment(i).toDate(),
                    }));
                  }}
                  setend_date={(i: any) => {
                    setFilters((e: any) => ({
                      ...e,
                      endDate: moment(i).toDate(),
                    }));
                  }}
                />
              </Stack>
            </Grid2>
          </Stack>
        </Grid2>
        <VirtualizedTable
          data={mergedData}
          columns={columns}
          fetchNextPage={() => Promise.resolve()}
          isLoading={isLoading || isFetching}
        />
      </Grid2>
    </>
  );
};

export default PayableOverdues;
